"use client";

import React from "react";

import { useDatePickerState } from "./hooks/useDatePickerState";

import { cn } from "../../../lib/utils";

// atoms
import type { InputSize } from "../../atoms/InputBase";
import { InputDate } from "../../atoms/InputDate";
// molecules
import { Calendar } from "../Calendar";
// context
import { useInputField } from "../InputWrapper/InputContext";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";

type BaseDatePickerProps = {
	// Size
	/**
	 * Size variant for the date picker input.
	 * @default "medium"
	 */
	size?: InputSize;
	// Core Props
	/**
	 * The id of the date picker
	 */
	id?: string;
	/**
	 * The disabled state of the date picker
	 */
	disabled?: boolean;
	/**
	 * The placeholder text
	 * @default "mm/dd/yyyy"
	 */
	placeholder?: string;
	/**
	 * Whether to show a clear button
	 * @default false
	 */
	clearable?: boolean;

	// Date Constraints
	/**
	 * The start date (inclusive)
	 */
	startDate?: Date;
	/**
	 * The end date (inclusive)
	 */
	endDate?: Date;

	// Calendar Features
	/**
	 * Whether to show month/year dropdowns for navigation
	 * @default false
	 */
	showMonthAndYearDropdowns?: boolean;
	/**
	 * Whether to show time selection dropdowns
	 * @default false
	 */
	showTimeDropdown?: boolean;
	/**
	 * Default time to use (hour 0-23) when showTimeDropdown is true
	 * @default 12
	 */
	defaultTime?: number;

	// Event Handlers
	/**
	 * The onBlur callback of the date picker
	 */
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

	// Styling
	/**
	 * The class name to apply to the calendar
	 */
	dangerouslySetClassName?: string;
	/**
	 * Additional class name to apply to the popover content (e.g. for z-index overrides)
	 */
	popoverClassName?: string;
	/**
	 * Whether the input should display in an error state with error styling
	 * @default false
	 */
	errored?: boolean;
};

type DatePickerProps = BaseDatePickerProps &
	(
		| {
				/**
				 * Whether to allow empty/null values
				 */
				allowEmpty: true;
				/**
				 * The selected date value
				 */
				value: Date | null;
				/**
				 * Callback when date is selected
				 */
				onChange: (date: Date | null) => void;
		  }
		| {
				/**
				 * Whether to allow empty/null values
				 */
				allowEmpty?: false;
				/**
				 * The selected date value
				 */
				value: Date;
				/**
				 * Callback when date is selected
				 */
				onChange: (date: Date) => void;
		  }
	);

/**
 * DatePicker component that combines InputBase with a Calendar popover
 * Similar to shadcn's date picker but using PerformYard components
 */
export const DatePicker: React.FC<DatePickerProps> = ({
	size,
	value,
	onChange,
	allowEmpty = false,
	startDate,
	endDate,
	disabled,
	placeholder = "mm/dd/yyyy",
	id,
	showMonthAndYearDropdowns = false,
	showTimeDropdown = false,
	defaultTime = 12,
	dangerouslySetClassName,
	popoverClassName,
	clearable = false,
	errored,
	...inputProps
}) => {
	// Input context integration
	const { id: contextId, error, disabled: contextDisabled } = useInputField();
	const generatedId = React.useId();
	const inputId = id || contextId || generatedId;

	// State management
	const { open, setOpen, month, setMonth, inputValue, handleInputChange, handleInputComplete, handleDateSelect } =
		useDatePickerState(value, onChange, allowEmpty, startDate, endDate, showTimeDropdown);

	// Error and disabled state
	const hasError = !!error || errored;
	const isDisabled = disabled || contextDisabled || false;

	const handleInputBlur = () => {
		handleInputComplete();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setOpen(true);
		} else if (e.key === "Escape") {
			e.preventDefault();
			setOpen(false);
		} else if (e.key === "Enter") {
			e.preventDefault();
			handleInputComplete();
			// Blur the input to close any mobile keyboards
			e.currentTarget.blur();
		}
	};

	// The selected date to display in the calendar
	// Only show a date as selected if there's an actual value
	const calendarSelectedDate = React.useMemo(() => {
		return value || undefined;
	}, [value]);

	const handleIconClick = () => {
		if (!isDisabled) {
			setOpen(true);
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<InputDate
					{...inputProps}
					size={size}
					id={inputId}
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleInputBlur}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={isDisabled}
					data-testid={`${inputId}-date-picker`}
					clearable={clearable}
					dangerouslySetClassName={dangerouslySetClassName}
					errored={hasError}
					onIconClick={handleIconClick}
				/>
			</PopoverTrigger>

			<PopoverContent className={cn("w-auto overflow-hidden p-0", popoverClassName)} align="end" sideOffset={8}>
				<Calendar
					mode="single"
					selected={calendarSelectedDate}
					month={month}
					onMonthChange={setMonth}
					onSelect={handleDateSelect}
					showMonthAndYearDropdowns={showMonthAndYearDropdowns}
					showTimeDropdown={showTimeDropdown}
					defaultTime={defaultTime}
					startDate={startDate}
					endDate={endDate}
				/>
			</PopoverContent>
		</Popover>
	);
};

export type { DatePickerProps };
